'use client';

import React, { useEffect, useRef, useState } from 'react';

import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { TextField } from '@/blocks/components/Controls/TextField';
import { LinkEmail } from '@/blocks/components/PlatformLink/LinkEmail';
import { validateEmail } from '@/blocks/forms/validation';
import { captchaKey, emailComplaints } from '@/global/public-settings';
import buttons from '@/styles/button.module.scss';
import links from '@/styles/link.module.scss';
import typography from '@/styles/typography.module.scss';

import ModalComplaints from '@/app/[lang]/complaints/sections/ComplaintsFormSection/Form/Modal';

import styles from './styles.module.scss';

const token = 'xpzvblvd';

interface Form {
  name: string;
  email: string;
  subject: string;
  accountNo?: string;
  message: string;
}

type BadResponse = {
  error: string;
  errors: { code: string; field: keyof Form; message: string }[];
};

enum FormSendingStatus {
  ERROR,
  SUCCESS,
}

export default function Form() {
  const tr = useTranslations('pages.complaints.section.formSection.form');
  const [sendingStatus, setSendingStatus] = useState<FormSendingStatus | null>(null);
  const [captchaResponse, setCaptchaResponse] = useState('');
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(tr('required.name')),
    email: Yup.string().required(tr('required.email')).test('email', tr('invalid.email'), validateEmail),
    subject: Yup.string().required(tr('required.subject')),
    accountNo: Yup.string(),
    message: Yup.string()
      .required(tr('required.message'))
      .min(8, tr('min.message'))
      .max(3000, tr('max.message')),
  });
  const refModalsContainer = useRef<null | HTMLDivElement>(null);

  function handleCaptchaChange(value: any) {
    if (typeof value === 'string') {
      setCaptchaResponse(value);
    }
  }

  function handleCaptchaExpired() {
    setCaptchaResponse('');
  }

  const { control, handleSubmit, reset, setError, formState } = useForm<Form>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
    shouldUseNativeValidation: false,
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      accountNo: '',
      message: '',
    },
  });

  useEffect(() => {
    if (sendingStatus !== null) {
      refModalsContainer.current?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [sendingStatus]);

  const makeCaptchaRequired = () => {
    const el = document.getElementById('g-recaptcha-response');
    if (el) {
      el.setAttribute('required', 'required');
    }
  };

  const onSubmit: SubmitHandler<Form> = async (data, event) => {
    const url = `https://formspree.io/f/${token}`;

    setSendingStatus(null);

    let response;

    try {
      response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          Accept: 'application/json',
        },
      });
    } catch (e) {
      console.error(e);
      setSendingStatus(FormSendingStatus.ERROR);
      return;
    }

    if (response.ok) {
      reset();
      setSendingStatus(FormSendingStatus.SUCCESS);
      return;
    }

    const parsedResponse: BadResponse | null = await response.json().catch((e) => {
      console.error(e);
      return null;
    });

    if (
      parsedResponse &&
      parsedResponse.error &&
      Array.isArray(parsedResponse.errors) &&
      // only known fields
      parsedResponse.errors.filter((e) => formState.defaultValues?.hasOwnProperty(e.field)).length
    ) {
      console.error(parsedResponse);
      return parsedResponse.errors.forEach((e) => {
        setError(e.field, { type: 'custom', message: e.message });
      });
    }

    setSendingStatus(FormSendingStatus.ERROR);
  };

  return (
    <div className={styles.wrapperForm}>
      <h4 className={classNames(typography.h4, styles.formTitle)}>{tr.rich('title')}</h4>
      <form action="/" className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={'name'}
          control={control}
          render={({ field: { ref, ...props }, fieldState: { error } }) => (
            <TextField
              label={tr.rich('labelName')}
              required
              className={styles.commonInputStyles}
              inputRef={ref}
              error={Boolean(error)}
              helperText={error?.message}
              {...props}
            />
          )}
        />
        <Controller
          name={'email'}
          control={control}
          render={({ field: { ref, ...props }, fieldState: { error } }) => (
            <TextField
              label={tr.rich('labelEmail')}
              required
              type={'email'}
              className={styles.commonInputStyles}
              inputRef={ref}
              error={Boolean(error)}
              helperText={error?.message}
              {...props}
            />
          )}
        />
        <Controller
          name={'subject'}
          control={control}
          render={({ field: { ref, ...props }, fieldState: { error } }) => (
            <TextField
              label={tr.rich('labelSubject')}
              required
              className={styles.commonInputStyles}
              inputRef={ref}
              error={Boolean(error)}
              helperText={error?.message}
              {...props}
            />
          )}
        />
        <Controller
          name={'accountNo'}
          control={control}
          render={({ field: { ref, ...props }, fieldState: { error } }) => (
            <TextField
              label={tr.rich('labelAccountNo')}
              className={styles.commonInputStyles}
              inputRef={ref}
              error={Boolean(error)}
              helperText={error?.message}
              {...props}
            />
          )}
        />
        <Controller
          name={'message'}
          control={control}
          render={({ field: { ref, ...props }, fieldState: { error } }) => (
            <TextField
              label={tr.rich('labelMessage')}
              required
              minRows={5}
              maxRows={25}
              multiline={true}
              className={styles.commonInputStyles}
              inputRef={ref}
              error={Boolean(error)}
              helperText={error?.message}
              {...props}
            />
          )}
        />
        {captchaKey ? (
          <ReCAPTCHA
            className={styles.captcha}
            sitekey={captchaKey}
            onChange={handleCaptchaChange}
            onExpired={handleCaptchaExpired}
            asyncScriptOnLoad={makeCaptchaRequired}
          />
        ) : null}
        <button
          className={classNames(buttons.btn, buttons.btnPrimary, buttons.btnResponsiveSM)}
          disabled={!formState.isValid || captchaResponse.length === 0}
        >
          {tr.rich('button')}
        </button>
      </form>
      <div ref={refModalsContainer}>
        {sendingStatus === FormSendingStatus.SUCCESS ? (
          <ModalComplaints
            modalTitle={tr.rich('modal.title')}
            modalSubtitle={tr.rich('modal.subtitle')}
            onClose={() => setSendingStatus(null)}
          />
        ) : null}
        {sendingStatus === FormSendingStatus.ERROR ? (
          <ModalComplaints
            modalTitle={tr.rich('modal.titleError')}
            modalSubtitle={tr.rich('modal.subtitleError', {
              span: (content) => <LinkEmail className={classNames(links.textLink)} email={emailComplaints} />,
            })}
            onClose={() => setSendingStatus(null)}
            errorStyle={true}
          />
        ) : null}
      </div>
    </div>
  );
}
